<template>
  <div class="bg-white shadow-md rounded-xl p-4 m-4">
    <MonkeyLoader v-if="isLoading" />
    <h2 class="text-xl font-semibold mb-4 text-gray-800">Entry Miscellaneous Company</h2>
    <form @submit.prevent="save">
      <div class="grid grid-cols-1 gap-4">
        <div class="form-section">
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="company">
              Company:
            </label>
            <input
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="company" type="text" v-model="company" ref="companyInput" @blur="validateField('company')" />
            <p v-if="!valid.company" class="text-red-500 text-xs italic">{{ validationMessages.company }}</p>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="type">
              Type:
            </label>
            <select
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="type" v-model="type" ref="typeSelect" @blur="validateField('type')">
              <option value="">-- Select Type --</option>
              <option value="1">Bank</option>
              <option value="2">Competitor</option>
              <option value="3">Forwarder</option>
              <option value="4">Subcon</option>
            </select>
            <p v-if="!valid.type" class="text-red-500 text-xs italic">{{ validationMessages.type }}</p>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="address">
              Address:
            </label>
            <textarea
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="address" rows="3" v-model="address" ref="addressTextarea" @blur="validateField('address')"></textarea>
            <p v-if="!valid.address" class="text-red-500 text-xs italic">{{ validationMessages.address }}</p>
          </div>

          <div class="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="country">
                Country:
              </label>
              <select
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="country" v-model="country" ref="countrySelect" @blur="validateField('country')">
                <option value="">-- Select Country --</option>
                <option v-for="countryItem in countries" :key="countryItem.id" :value="countryItem.id">
                  {{ countryItem.countryName }}
                </option>
              </select>
              <p v-if="!valid.country" class="text-red-500 text-xs italic">{{ validationMessages.country }}</p>
            </div>

            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="city">City:</label>
              <div class="flex items-center">
                <select
                  class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  id="city" v-model="city" ref="citySelect" @blur="validateField('city')">
                  <option value="">-- Select City --</option>
                  <option v-for="c in cities" :key="c.id" :value="c.id">{{ c.cityName }}</option>
                </select>
                <button @click="openModal('city')" type="button"
                  class="ml-2 bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-3 rounded text-sm focus:outline-none focus:shadow-outline">
                  +
                </button>
              </div>
              <p v-if="!valid.city" class="text-red-500 text-xs italic mt-1">{{ validationMessages.city }}</p>
            </div>

            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="zipCode">
                ZIP Code:
              </label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="zipCode" type="text" v-model="zipCode" ref="zipCodeInput" @blur="validateField('zipCode')" />
              <p v-if="!valid.zipCode" class="text-red-500 text-xs italic">{{ validationMessages.zipCode }}</p>
            </div>
          </div>

          <div class="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="phone">
                Phone Number:
              </label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="phone" type="text" v-model="phone" placeholder="e.g.: 5862143" ref="phoneInput"
                @blur="validateField('phone')" />
              <p v-if="!valid.phone" class="text-red-500 text-xs italic">{{ validationMessages.phone }}</p>
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="email">
                Email:
              </label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="email" type="email" v-model="email" ref="emailInput" @blur="validateField('email')" />
              <p v-if="!valid.email" class="text-red-500 text-xs italic">{{ validationMessages.email }}</p>
            </div>
          </div>

          <div class="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="extension">
                Extension:
              </label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="extension" type="text" v-model="extension" placeholder="e.g.: 123,234" ref="extensionInput"
                @blur="validateField('extension')" />
              <p v-if="!valid.extension" class="text-red-500 text-xs italic">{{ validationMessages.extension }}</p>
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="fax">
                Fax:
              </label>
              <input
                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="fax" type="text" v-model="fax" ref="faxInput" @blur="validateField('fax')" />
              <p v-if="!valid.fax" class="text-red-500 text-xs italic">{{ validationMessages.fax }}</p>
            </div>
          </div>


          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="term">
              Term:
            </label>
            <input
              class="input-border shadow appearance-none border rounded w-1/4 py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="term" type="number" v-model="term" ref="termInput" @blur="validateField('term')" />
            <span class="ml-2">days</span>
            <p v-if="!valid.term" class="text-red-500 text-xs italic">{{ validationMessages.term }}</p>
          </div>

          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2" for="memo">
              Memo:
            </label>
            <textarea
              class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              id="memo" rows="3" v-model="memo" ref="memoTextarea" @blur="validateField('memo')"></textarea>
            <p v-if="!valid.memo" class="text-red-500 text-xs italic">{{ validationMessages.memo }}</p>
          </div>

          <div class="mb-4 flex justify-end">
            <button type="button" @click="addContact"
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline">
              Add Contact
            </button>
          </div>

          <div class="overflow-x-auto mb-4">
            <table class="min-w-full table-auto">
              <thead>
                <tr>
                  <th class="px-4 py-2 border-b">Name</th>
                  <th class="px-4 py-2 border-b">Handphone</th>
                  <th class="px-4 py-2 border-b">Email</th>
                  <th class="px-4 py-2 border-b">Bank</th>
                  <th class="px-4 py-2 border-b">Rekening</th>
                  <th class="px-4 py-2 border-b">NPWP</th>
                  <th class="px-4 py-2 border-b">Job Title</th>
                  <th class="px-4 py-2 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(contact, index) in contacts" :key="index">
                  <td class="px-4 py-2 border-b">
                    <input type="text" v-model="contact.name" class="w-full border rounded p-2"
                      placeholder="Enter Name" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <input type="text" v-model="contact.handphone" class="w-full border rounded p-2"
                      placeholder="Enter Handphone" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <input type="email" v-model="contact.email" class="w-full border rounded p-2"
                      placeholder="Enter Email (Optional)" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <select v-model="contact.bankId" class="w-full border rounded p-2">
                      <option value="">-- Pilih Bank --</option>
                      <option v-for="bank in banks" :key="bank.id" :value="bank.id">
                        {{ bank.bankName }}
                      </option>
                    </select>
                  </td>
                  <td class="px-4 py-2 border-b">
                    <input type="text" v-model="contact.rekening" class="w-full border rounded p-2"
                      placeholder="Enter Rekening" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <input type="text" v-model="contact.npwp" class="w-full border rounded p-2"
                      placeholder="Enter NPWP" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <input type="text" v-model="contact.jobTitle" class="w-full border rounded p-2"
                      placeholder="Enter Job Title" />
                  </td>
                  <td class="px-4 py-2 border-b">
                    <button type="button" @click="removeContact(index)"
                      class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded text-sm focus:outline-none focus:shadow-outline">
                      Remove
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-if="!valid.contacts" class="text-red-500 text-xs italic mt-1">{{ validationMessages.contacts }}</p>
          </div>
        </div>
      </div>

      <div class="flex justify-center mt-4">
        <button
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
          type="submit" :disabled="!isFormValid">
          Save
        </button>
        <button
          class="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          @click="cancel">
          Cancel
        </button>
      </div>
    </form>

    <add-item-modal :visible="isModalVisible" :title="modalConfig.title" :label="modalConfig.label"
      :existing-items="modalConfig.existingItems" :name-key="modalConfig.nameKey" @close="closeModal"
      @save="handleModalSave" />
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import AddItemModal from './AddItemModal.vue';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
  name: 'SubconEntry',
  components: { AddItemModal, MonkeyLoader },
  data() {
    return {
      // Form fields
      company: '',
      type: '4', // Default to 'Subcon'
      address: '',
      country: '77', // Default country set to 77 (Indonesia)
      city: '',
      zipCode: '',
      phone: '',
      fax: '', // Optional
      extension: '', // Optional
      email: '', // Optional for main form
      term: 1,
      memo: '',
      contacts: [], // Array to store contact persons

      // Data for dropdowns
      countries: [],
      cities: [],
      banks: [],

      // Validation state: true if valid, false if invalid
      valid: {
        company: true, type: true, address: true, country: true, city: true,
        zipCode: true, phone: true,
        email: true, // Validation logic will allow empty string
        extension: true, // Always true if no format validation
        fax: true, // Always true if no format validation
        term: true, memo: true, contacts: true,
      },
      // Validation messages to display
      validationMessages: {
        company: '', type: '', address: '', country: '', city: '',
        zipCode: '', phone: '',
        email: '',
        extension: '',
        fax: '',
        term: '', memo: '', contacts: '',
      },
      isFormValid: false, // Overall form validity state

      // State for the modal (for adding City)
      isModalVisible: false,
      modalConfig: {
        title: '',
        label: '',
        type: '', // modal type is 'city'
        existingItems: [],
        nameKey: ''
      },
    };
  },
  methods: {
    // Validates a specific field and updates its validity state and message
    validateField(fieldName) {
      // Reset validity state and message for the current field
      this.valid[fieldName] = true;
      this.validationMessages[fieldName] = '';

      switch (fieldName) {
        case 'company':
          if (!this.company || !this.company.trim()) {
            this.valid.company = false;
            this.validationMessages.company = 'Company is required.';
          }
          break;
        case 'type':
          if (!this.type) {
            this.valid.type = false;
            this.validationMessages.type = 'Type is required.';
          }
          break;
        case 'address':
          if (!this.address || !this.address.trim()) {
            this.valid.address = false;
            this.validationMessages.address = 'Address is required.';
          }
          break;
        case 'country':
          if (!this.country) {
            this.valid.country = false;
            this.validationMessages.country = 'Country is required.';
          }
          break;
        case 'city':
          if (!this.city) {
            this.valid.city = false;
            this.validationMessages.city = 'City is required.';
          }
          break;
        case 'zipCode':
          if (!this.zipCode || !this.zipCode.trim()) {
            this.valid.zipCode = false;
            this.validationMessages.zipCode = 'ZIP Code is required.';
          }
          break;
        case 'phone':
          if (!this.phone || !this.phone.trim()) {
            this.valid.phone = false;
            this.validationMessages.phone = 'Phone Number is required.';
          }
          break;
        case 'email':
          // Email is optional in the main form, but if filled, must be a valid format
          if (this.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
            this.valid.email = false;
            this.validationMessages.email = 'Invalid email format.';
          }
          break;
        case 'fax':
        case 'extension':
          // Fax and Extension are optional, no specific format validation applied for now
          break;
        case 'term':
          if (this.term === null || this.term === '' || isNaN(this.term) || parseInt(this.term) < 1) {
            this.valid.term = false;
            this.validationMessages.term = 'Term must be a positive number (days).';
          }
          break;
        case 'memo':
          if (!this.memo || !this.memo.trim()) {
            this.valid.memo = false;
            this.validationMessages.memo = 'Memo is required.';
          }
          break;
        case 'contacts':
          // All fields within contacts table are mandatory, EXCEPT Email
          if (this.contacts.length === 0) {
            this.valid.contacts = false;
            this.validationMessages.contacts = 'At least one contact must be added.';
          } else {
            let allContactsValid = true;
            for (let i = 0; i < this.contacts.length; i++) {
              const contact = this.contacts[i];
              // Hanya validasi kontak yang "aktif" (status true) atau yang belum ada ID (baru)
              if (contact.status === false) continue; // Skip if marked for soft delete

              // Check mandatory fields EXCEPT email
              if (
                !contact.name || !contact.name.trim() ||
                !contact.handphone || !contact.handphone.trim() ||
                !contact.rekening || !contact.rekening.trim() ||
                !contact.npwp || !contact.npwp.trim() ||
                !contact.jobTitle || !contact.jobTitle.trim() ||
                !contact.bankId // Bank ID for contact is also mandatory
              ) {
                allContactsValid = false;
                this.valid.contacts = false;
                this.validationMessages.contacts = `All required fields (Name, Handphone, Rekening, NPWP, Job Title, Bank) for contact #${i + 1} must be filled.`;
                break;
              }
              // Email for contact is now optional, but if filled, must be a valid format
              if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
                allContactsValid = false;
                this.valid.contacts = false;
                this.validationMessages.contacts = `Invalid email format for contact #${i + 1}.`;
                break;
              }
            }
            this.valid.contacts = allContactsValid;
            if (allContactsValid) {
              this.validationMessages.contacts = '';
            }
          }
          break;
      }
      this.updateFormValidity();
    },

    // Updates the overall form validity by checking all individual field validities
    updateFormValidity() {
      this.isFormValid = Object.values(this.valid).every(v => v);
    },

    // Adds a new empty contact row to the contacts table
    addContact() {
      this.contacts.push({
        name: '', handphone: '', email: '', rekening: '', npwp: '', jobTitle: '', bankId: ''
      });
      // Immediately validate contacts field to show messages for new empty rows
      this.validateField('contacts');
    },

    // Removes a contact row from the table (soft delete implemented in SubconUpdate, this is a simple remove)
    removeContact(index) {
      this.contacts.splice(index, 1);
      this.validateField('contacts'); // Re-validate contacts after removal
    },

    // Handles the form submission
    async save() {
      // Trigger validation for all fields before attempting to save
      Object.keys(this.valid).forEach(field => this.validateField(field));

      // After all individual validations, re-check overall form validity
      this.updateFormValidity();

      if (!this.isFormValid) {
        // If form is not valid, show a general warning and try to focus on the first invalid field
        const firstInvalidField = Object.keys(this.valid).find(key => !this.valid[key]);
        if (firstInvalidField) {
          let refElement = null;
          // Attempt to find the specific input element reference
          if (this.$refs[firstInvalidField]) {
            refElement = this.$refs[firstInvalidField];
          } else if (this.$refs[firstInvalidField + 'Input']) {
            refElement = this.$refs[firstInvalidField + 'Input'];
          } else if (this.$refs[firstInvalidField + 'Select']) {
            refElement = this.$refs[firstInvalidField + 'Select'];
          } else if (this.$refs[firstInvalidField + 'Textarea']) {
            refElement = this.$refs[firstInvalidField + 'Textarea'];
          }

          if (refElement && typeof refElement.focus === 'function') {
            this.$nextTick(() => {
              refElement.focus();
            });
          }
        }
        Swal.fire({
          icon: 'warning',
          title: 'Validation Error',
          text: 'Please fill in all required fields correctly.',
        });
        return; // Stop the save process
      }

      try {
        const payload = {
          EntityName: this.company,
          EntityType: parseInt(this.type),
          EntityAddress: this.address,
          CountryId: parseInt(this.country),
          CityId: parseInt(this.city),
          ZipCode: this.zipCode,
          EntityPhone: this.phone,
          EntityFax: this.fax || null,
          EntityExt: this.extension || null,
          EntityEmail: this.email || null, // Main form email is already optional
          Term: this.term.toString(),
          Memo: this.memo,
          Contacts: this.contacts.map(c => ({
            ContactName: c.name,
            ContactPhone: c.handphone,
            ContactEmail: c.email || null, // Ensure email is sent as null if empty for contacts
            ContactRek: c.rekening,
            ContactNPWP: c.npwp,
            ContactJobTitle: c.jobTitle,
            BankId: parseInt(c.bankId)
          }))
        };

        const response = await axios.post(`${window.location.origin}/api/entities`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        console.log('Saved successfully:', response.data);
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Data saved successfully!',
        });
        this.resetForm();
      } catch (error) {
        console.error('Failed to save:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save data. Please try again.',
        });
      }
    },

    // Resets the form to its initial state
    cancel() {
      this.resetForm();
    },

    // Resets all form fields and validation states
    resetForm() {
      this.company = '';
      this.type = '4';
      this.address = '';
      this.country = '77'; // Reset default country ke 77
      this.city = '';
      this.zipCode = '';
      this.fax = '';
      this.phone = '';
      this.extension = '';
      this.email = '';
      this.term = 1;
      this.memo = '';
      this.contacts = [];

      // Reset all validation states
      Object.keys(this.valid).forEach(field => {
        this.valid[field] = true;
        this.validationMessages[field] = '';
      });
      this.isFormValid = false;
    },

    // --- API Fetch Methods ---
    async fetchCountries() {
      try {
        const response = await axios.get(`${window.location.origin}/tblcountries`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.countries = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching countries:', error);
      }
    },
    async fetchCities() {
      try {
        const response = await axios.get(`${window.location.origin}/tblcities`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.cities = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching cities:', error);
      }
    },
    async fetchBanks() {
      try {
        const response = await axios.get(`${window.location.origin}/api/banks`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.banks = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error('Error fetching banks:', error);
      }
    },

    // --- Modal Methods ---
    openModal(type) {
      switch (type) {
        case 'city':
          this.modalConfig = { title: 'Add New City', label: 'City Name', type: 'city', existingItems: this.cities, nameKey: 'cityName' };
          break;
        default:
          console.error("Unknown modal type:", type);
          return;
      }
      this.isModalVisible = true;
    },

    closeModal() {
      this.isModalVisible = false;
    },

    async handleModalSave(newItemName) {
      const { type } = this.modalConfig;
      let payload = {};
      let url = '';

      switch (type) {
        case 'city':
          url = `${window.location.origin}/tblcities`;
          payload = { cityName: newItemName, countryId: this.country };
          break;
        default:
          console.error("Unknown modal type:", type);
          return;
      }

      try {
        console.log('Attempting to save new item:', payload);
        const response = await axios.post(url, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        const newRecord = response.data;
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: `${this.modalConfig.label} saved successfully!`,
        });
        this.closeModal();

        switch (type) {
          case 'city':
            await this.fetchCities();
            this.city = newRecord.id;
            break;
        }
      } catch (error) {
        console.error(`Failed to save ${type}:`, error);
        if (error.response && error.response.status === 409) {
          Swal.fire({
            icon: 'error',
            title: 'Conflict',
            text: `A ${type} with this name already exists in the database.`,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: `Failed to save ${this.modalConfig.label}. Please try again.`,
          });
        }
      }
    },
  },
  mounted() {
    this.fetchCountries();
    this.fetchCities();
    this.fetchBanks();

    if (this.$refs.companyInput) {
      this.$refs.companyInput.focus();
    }

    this.validateField('company'); // Initial validation
    this.updateFormValidity(); // Initial validity check
  },
  watch: {
    company: function () { this.validateField('company'); },
    type: function () { this.validateField('type'); },
    address: function () { this.validateField('address'); },
    country: function () { this.validateField('country'); },
    city: function () { this.validateField('city'); },
    zipCode: function () { this.validateField('zipCode'); },
    phone: function () { this.validateField('phone'); },
    email: function () { this.validateField('email'); }, // Email main form tetap divalidasi formatnya jika diisi
    extension: function () { this.validateField('extension'); },
    fax: function () { this.validateField('fax'); },
    term: function () { this.validateField('term'); },
    memo: function () { this.validateField('memo'); },
    contacts: {
      handler: function () { this.validateField('contacts'); },
      deep: true
    },
    isFormValid(newValue) {
      const saveButton = this.$el.querySelector('button[type="submit"]');
      if (saveButton) {
        saveButton.disabled = !newValue;
      }
    },
  },
};
</script>

<style scoped>
.input-border {
  border-bottom: 2px solid #ccc;
  border-radius: 0;
  border-left: none;
  border-right: none;
  border-top: none;
}

.input-border:focus {
  border-bottom-color: #007bff;
  box-shadow: none;
}

.form-section {
  display: flex;
  flex-direction: column;
}

.form-section>div {
  margin-bottom: 0.75rem;
}

.form-section>div:last-child {
  margin-bottom: 0;
}
</style>